import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type {
  ConnectionDetailResponse,
  ConnectionRequestsResponse,
  ConnectionsResponse,
} from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * **Everything here needs a session**, and unlike the roster there is no signed-out version of
 * any of it: a connection is the most private thing this platform knows about two people.
 *
 * **No path carries a member id.** The reader is the session; the person being read about is
 * named by `connectionRef` or `requesterRef`, which the API derives and the client only ever
 * echoes back. Every segment goes through `encodeURIComponent`.
 *
 * **There is no hook here that sends a message, opens a thread, or counts something unread — they
 * live in `domains/messages`, against `/api/messages`.** This used to be an absence rather than a
 * boundary: ADR 032 meant there were no such hooks anywhere. ADR 052 supersedes it and `MEM-054`
 * built them one slice over, so what holds here now is only that this file mirrors
 * `/api/connections`. The request's note is still a field on {@link useSendConnectionRequest}'s
 * body rather than a message send — it is written to somebody the member is not yet connected to,
 * and it becomes the thread's first message only when the recipient accepts (`MEM-034` AC-6).
 */

const CONNECTIONS_KEY = ['connections', 'mine'] as const;
const REQUESTS_KEY = ['connections', 'requests'] as const;
const connectionKey = (connectionRef: string): readonly unknown[] => [
  'connections',
  'detail',
  connectionRef,
];

/** `MEM-033` AC-1 — the member's full set, across every event. */
export function useMyConnections(options: {
  readonly enabled: boolean;
}): UseQueryResult<ConnectionsResponse> {
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: () => apiRequest<ConnectionsResponse>('/api/connections'),
    enabled: options.enabled,
    retry: false,
  });
}

/** `MEM-033` AC-2 — one connection, and the only response that carries the reader's note. */
export function useConnection(
  connectionRef: string | null,
  options: { readonly enabled: boolean },
): UseQueryResult<ConnectionDetailResponse> {
  return useQuery({
    queryKey: connectionKey(connectionRef ?? ''),
    queryFn: () =>
      apiRequest<ConnectionDetailResponse>(
        `/api/connections/${encodeURIComponent(connectionRef ?? '')}`,
      ),
    enabled: options.enabled && (connectionRef ?? '').length > 0,
    retry: false,
  });
}

/** `MEM-032` AC-6 — the inbound requests screen. Pending only; the API serves nothing else. */
export function useMyConnectionRequests(options: {
  readonly enabled: boolean;
}): UseQueryResult<ConnectionRequestsResponse> {
  return useQuery({
    queryKey: REQUESTS_KEY,
    queryFn: () => apiRequest<ConnectionRequestsResponse>('/api/connections/requests'),
    enabled: options.enabled,
    retry: false,
  });
}

/**
 * `MEM-033` AC-3 — save the private note.
 *
 * `PUT`, and it **returns the whole detail**, so the screen renders the note the server stored
 * rather than the one it sent. The response replaces the cached detail, and the list is
 * invalidated with it — the connections card draws the member's own note (`Web App.dc.html`
 * §3.15), so a save made from the card's composer has to reach the card that opened it.
 */
export function useSaveConnectionNote(
  connectionRef: string,
): UseMutationResult<ConnectionDetailResponse, Error, { readonly note: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ note }) =>
      apiRequest<ConnectionDetailResponse>(
        `/api/connections/${encodeURIComponent(connectionRef)}/note`,
        { method: 'PUT', body: { note } },
      ),
    onSuccess: async (data) => {
      queryClient.setQueryData(connectionKey(connectionRef), data);
      await queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

/**
 * `MEM-034` AC-1, AC-2 — removal.
 *
 * The detail's cache entry is dropped rather than refetched: the connection is gone from the
 * reader's list, so a refetch would be a 404 the screen has to explain after it has already
 * navigated away.
 */
export function useRemoveConnection(
  connectionRef: string,
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiRequest<void>(`/api/connections/${encodeURIComponent(connectionRef)}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: connectionKey(connectionRef) });
      await queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

/**
 * `MEM-032` AC-2, AC-5 — send a request.
 *
 * The API answers 204 whatever became of it, so there is nothing to read from success beyond the
 * fact that it was accepted. That is `MEM-032` AC-7's silence applied to the *sender*: a
 * response that distinguished "delivered" from "you have asked before" would be the notification
 * the requirement removes, arriving on the wrong side.
 *
 * Nothing is invalidated. A sent request does not appear on any screen this member can open —
 * there is no outbound view, deliberately (see the API's `repository.ts`) — so there is no cache
 * entry that has gone stale.
 */
export function useSendConnectionRequest(): UseMutationResult<
  void,
  Error,
  { readonly eventId: string; readonly attendeeRef: string; readonly message: string }
> {
  return useMutation({
    mutationFn: async (body) => {
      await apiRequest<void>('/api/connections/requests', { method: 'POST', body });
    },
  });
}

/**
 * `MEM-032` AC-7 — Accept and Decline.
 *
 * One mutation for both, taking the answer rather than exposing two hooks, for the reason
 * `useSetMeetListEntry` gives: two hooks let a screen call the wrong one from a stale render.
 * Here the cost of that would be answering a request the opposite way from the button pressed.
 *
 * Both invalidate the requests list; only an accept can change the connections list, but both
 * invalidate it anyway — a conditional invalidation is a branch that can be wrong, and the read
 * is cheap.
 */
export function useAnswerConnectionRequest(): UseMutationResult<
  void,
  Error,
  { readonly requesterRef: string; readonly answer: 'accept' | 'decline' }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requesterRef, answer }) => {
      await apiRequest<void>(
        `/api/connections/requests/${encodeURIComponent(requesterRef)}/${answer}`,
        { method: 'POST' },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
      await queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}
